import React, { forwardRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  products,
  categories,
  canAddToCart,
  defaultVariantId,
  getProductAvailability,
  homepageContent,
  siteContent,
  toOrderProduct
} from '../../data/products';
import { useCart, formatINR } from '../../context/CartContext';
import { buildWhatsAppUrl } from '../../utils/whatsapp';
import { Reveal } from '../../motion';
import Tilt3D from '../../motion3d/Tilt3D';
import { ParallaxImg } from '../../motion3d/Depth';
import Icon from '../Icon/Icon';
import './Menu.css';

const ProductCard = forwardRef(({ product, index }, ref) => {
  const { qtyOf, add, decrement } = useCart();
  const [variantId, setVariantId] = useState(() => defaultVariantId(product));
  const orderProduct = toOrderProduct(product, variantId);
  const qty = qtyOf(product.id, orderProduct.variantId);
  const orderOnly = Boolean(orderProduct.orderOnly);
  const canAdd = canAddToCart(orderProduct);
  const soldOut = product.availability === 'sold-out';
  const availability = getProductAvailability(product);
  const priceLabel = orderProduct.priceLabel || formatINR(orderProduct.price);
  const whatsappMessage = soldOut
    ? `Hi ${siteContent.brand.name}! Please let me know when ${product.name} is available again.`
    : `Hi ${siteContent.brand.name}! I'd like to order: ${orderProduct.name} (${priceLabel}).`;

  return (
    <motion.article
      ref={ref}
      className={`product-card availability-${availability.tone}`}
      layout
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -14 }}
      transition={{ duration: 0.55, ease: 'easeOut', delay: Math.min(index * 0.045, 0.35) }}
    >
      <Tilt3D className="product-card-inner" rotationFactor={5}>
        <div className="product-img-wrapper">
          <img src={product.image} alt={product.name} className="product-img" loading="lazy" />
          <span className={`product-status-badge status-${availability.tone}`}>
            <i aria-hidden="true" />
            {availability.label}
          </span>
          <AnimatePresence>
            {canAdd && qty > 0 && (
              <motion.span
                className="product-qty-flag"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
              >
                {qty} in cart
              </motion.span>
            )}
          </AnimatePresence>
          {canAdd && (
            <button className="img-add" onClick={() => add(product.id, orderProduct.variantId)} aria-label={`Add ${orderProduct.name}`}>
              <span>+</span>
            </button>
          )}
        </div>
        <div className="product-info">
          <div className="product-eyebrow">{product.category}</div>
          <h3 className="product-name">{product.name}</h3>
          <p className="product-desc">{product.desc}</p>
          {product.variants.length > 0 && (
            <label className="product-variant" htmlFor={`variant-${product.id}`}>
              <span>Choose an option</span>
              <select
                id={`variant-${product.id}`}
                value={orderProduct.variantId}
                onChange={(event) => setVariantId(event.target.value)}
              >
                <option value="">Standard - {product.priceLabel || formatINR(product.price)}</option>
                {product.variants.map((variant) => (
                  <option key={variant.id} value={variant.id}>
                    {variant.name} - {variant.priceOnRequest ? 'Price on request' : formatINR(variant.price)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="product-tags">
            {product.tags.map((t, i) => <span key={i} className="tag">{t}</span>)}
          </div>
          <div className="product-lead-time">
            <Icon name="clock" size={14} strokeWidth={1.7} />
            <span>{product.leadTime || siteContent.ordering.defaultLeadTime}</span>
          </div>
          <div className="product-foot">
            <div className="product-buy">
              <span className="product-price">{priceLabel}</span>
              <a
                className="order-now-link"
                href={buildWhatsAppUrl(siteContent.contact.whatsappNumber, whatsappMessage)}
                target="_blank"
                rel="noreferrer"
              >
                {soldOut ? 'Notify me' : orderOnly ? 'Order on WhatsApp' : 'Order Now'}
              </a>
            </div>
            <AnimatePresence mode="wait" initial={false}>
              {canAdd && qty === 0 ? (
                <motion.button
                  key="add"
                  className="add-btn"
                  onClick={() => add(product.id, orderProduct.variantId)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  whileTap={{ scale: 0.96 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                >
                  Add <span>+</span>
                </motion.button>
              ) : canAdd ? (
                <motion.div
                  key="stepper"
                  className="stepper card-stepper"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                >
                  <motion.button whileTap={{ scale: 0.92 }} onClick={() => decrement(product.id, orderProduct.variantId)} aria-label={`Decrease ${orderProduct.name} quantity`}>−</motion.button>
                  <motion.span
                    key={qty}
                    initial={{ y: -8, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                  >
                    {qty}
                  </motion.span>
                  <motion.button
                    whileTap={{ scale: 0.92 }}
                    onClick={() => add(product.id, orderProduct.variantId)}
                    aria-label={`Increase ${orderProduct.name} quantity`}
                    disabled={qty >= siteContent.ordering.maxQuantity}
                  >+</motion.button>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </div>
      </Tilt3D>
    </motion.article>
  );
});

const Menu = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const filtered = products.filter((product) => product.active !== false && (activeCategory === 'all' || product.category === activeCategory));

  return (
    <section id="menu" className="menu">
      <div className="menu-inner">
        <Reveal className="occasion-row" y={18}>
          {homepageContent.menu.occasions.map((occasion) => (
            <button
              key={`${occasion.categoryId}-${occasion.label}`}
              className={`occasion-tile ${activeCategory === occasion.categoryId ? 'active' : ''}`}
              onClick={() => setActiveCategory(activeCategory === occasion.categoryId ? 'all' : occasion.categoryId)}
              aria-pressed={activeCategory === occasion.categoryId}
            >
              <ParallaxImg src={occasion.image} alt="" aria-hidden="true" loading="lazy" strength={12} />
              <span>{occasion.label}</span>
            </button>
          ))}
        </Reveal>
        <div className="menu-header">
          <Reveal className="menu-title-wrap">
            <div className="section-tag">{homepageContent.menu.eyebrow}</div>
            <h2 className="section-title">{homepageContent.menu.title}</h2>
            <p className="section-subtitle">{homepageContent.menu.subtitle}</p>
          </Reveal>
          <Reveal className="category-filter" delay={0.15} y={18}>
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`cat-btn ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
                aria-pressed={activeCategory === cat.id}
              >
                {activeCategory === cat.id && (
                  <motion.span
                    className="cat-pill"
                    layoutId="cat-pill"
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
                <span className="cat-btn-label">{cat.label}</span>
              </button>
            ))}
          </Reveal>
        </div>
        <p className="menu-results" role="status" aria-live="polite">
          {filtered.length} {filtered.length === 1 ? 'bake' : 'bakes'} available to browse
        </p>
        <motion.div className="products-grid" layout>
          <AnimatePresence mode="popLayout">
            {filtered.map((product, i) => (
              <ProductCard key={product.id} product={product} index={i} />
            ))}
          </AnimatePresence>
        </motion.div>
        {filtered.length === 0 && (
          <div className="menu-empty" role="status">
            <p>No bakes are listed in this category today.</p>
            <button type="button" onClick={() => setActiveCategory('all')}>Browse all bakes</button>
          </div>
        )}
      </div>
    </section>
  );
};
export default Menu;
